import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import fa from "../locales/fa.json";
import en from "../locales/en.json";
import tr from "../locales/tr.json";

export const LANGS = [
  // { code: "fa", short: "FA", dir: "rtl" },
  { code: "en", short: "EN", dir: "ltr" },
  { code: "tr", short: "TR", dir: "ltr" },
];

const STORAGE_KEY = "appLang";
const dictionaries = { fa, en, tr };

const lookup = (obj, path) =>
  path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) return acc[key];
    return undefined;
  }, obj);

const isLang = (value) => value === "en" || value === "tr";

const readStoredLang = () => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isLang(stored) ? stored : "en";
};

const applyDocumentLang = (lang) => {
  if (typeof document === "undefined") return;
  const dir = lang === "fa" ? "rtl" : "ltr";
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(readStoredLang);

  useEffect(() => {
    applyDocumentLang(lang);
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!isLang(next)) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
    applyDocumentLang(next);
  }, []);

  const t = useCallback(
    (key) => {
      const fromCurrent = lookup(dictionaries[lang], key);
      if (fromCurrent !== undefined) return fromCurrent;
      const fromEn = lookup(dictionaries.en, key);
      return fromEn !== undefined ? fromEn : key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      dir: lang === "fa" ? "rtl" : "ltr",
      setLang,
      t,
    }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
};
